// Части экрана результата: превью оригинала, полоса скачивания,
// фильтр полей по статусу и строка «поле — оригинал — перевод».
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Minus, Plus, FileQuestion } from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

const ZOOMS = [1, 1.5, 2, 3];

// Скачивает файл как blob и держит на него object URL, пока не сменится
// url/version. Пока enabled = false, ничего не грузит.
const useBlobFile = (url, { enabled = true, version } = {}) => {
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!url || !enabled) return undefined;
    let objectUrl = null;
    let cancelled = false;
    setState({ status: "loading" });
    axios
      .get(url, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setState({ status: "ready", url: objectUrl, type: res.data.type });
      })
      .catch(() => !cancelled && setState({ status: "unavailable" }));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, enabled, version]);

  return state;
};

const VIEWS = [
  { key: "original", label: "Оригинал" },
  { key: "translation", label: "Перевод" },
];

// translationUrl — PDF перевода в том виде, в каком его скачают; если задан,
// в шапке появляется переключатель «Оригинал / Перевод». translationVersion
// меняется после сохранения правок, чтобы превью перевода пересобралось;
// translationStale — есть несохранённые правки, которых в превью ещё нет.
export const ScanPreview = ({
  recordId,
  translationUrl,
  translationVersion,
  translationStale = false,
}) => {
  const [zoom, setZoom] = useState(0);
  const [view, setView] = useState("original");
  const [translationOpened, setTranslationOpened] = useState(false);

  const original = useBlobFile(
    recordId ? `${API_BASE_URL}/api/passports/${recordId}/original` : null,
  );
  const translation = useBlobFile(translationUrl, {
    enabled: translationOpened,
    version: translationVersion,
  });

  useEffect(() => {
    setZoom(0);
    setView("original");
    setTranslationOpened(false);
  }, [recordId]);

  const showTranslation = Boolean(translationUrl) && view === "translation";
  const state = showTranslation ? translation : original;
  const isPdf = state.type === "application/pdf";

  const switchView = (key) => {
    setView(key);
    if (key === "translation") setTranslationOpened(true);
  };

  return (
    <aside className="res-preview" aria-label="Предпросмотр документа">
      <div className="res-preview-head">
        {translationUrl ? (
          <Segmented
            label="Что показать"
            className="is-light res-preview-switch"
            options={VIEWS}
            value={view}
            onChange={switchView}
          />
        ) : (
          <h2 className="res-preview-title">Оригинал</h2>
        )}
        {state.status === "ready" && !isPdf && (
          <>
            <button
              type="button"
              className="px-icon-btn is-small res-preview-btn"
              aria-label="Уменьшить"
              disabled={zoom === 0}
              onClick={() => setZoom((z) => Math.max(0, z - 1))}
            >
              <Minus size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="px-icon-btn is-small res-preview-btn"
              aria-label="Увеличить"
              disabled={zoom === ZOOMS.length - 1}
              onClick={() => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1))}
            >
              <Plus size={18} aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {showTranslation && translationStale && (
        <p className="res-preview-hint">
          Есть несохранённые правки — сохраните их, чтобы увидеть в превью
        </p>
      )}

      <div className={`res-preview-body${isPdf ? " is-pdf" : ""}`}>
        {state.status === "loading" && (
          <p className="res-preview-note">
            {showTranslation ? "Готовим перевод…" : "Загружаем оригинал…"}
          </p>
        )}
        {state.status === "unavailable" && (
          <p className="res-preview-note">
            <FileQuestion size={28} aria-hidden="true" />
            {showTranslation
              ? "Не удалось подготовить превью перевода"
              : "Предпросмотр недоступен для этого файла"}
          </p>
        )}
        {state.status === "ready" &&
          (isPdf ? (
            <iframe
              title={showTranslation ? "Перевод документа" : "Оригинал документа"}
              src={state.url}
              className="res-preview-pdf"
            />
          ) : (
            <div className="res-preview-scroll">
              <img
                src={state.url}
                alt="Скан исходного документа"
                style={{ width: `${ZOOMS[zoom] * 100}%` }}
              />
            </div>
          ))}
      </div>
    </aside>
  );
};

export const DownloadBar = ({ title = "Перевод готов", children }) => (
  <section className="res-download" aria-label="Скачать перевод">
    <span className="res-download-title">{title}</span>
    {children}
  </section>
);

export const Segmented = ({ label, options, value, onChange, className = "" }) => (
  <div className={`res-seg ${className}`.trim()} role="radiogroup" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        role="radio"
        aria-checked={value === o.key}
        className={value === o.key ? "is-active" : ""}
        onClick={() => onChange(o.key)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const FILTERS = [
  { value: "all", label: "Все поля" },
  { value: "uncertain", label: "Требуют проверки", attention: true },
  { value: "partial", label: "Частично" },
  { value: "not_found", label: "Не найдено" },
  { value: "complete", label: "Распознано" },
];

export const StatusFilterBar = ({ stats, value, onChange }) => {
  if (!stats || stats.total === 0) return null;
  return (
    <div className="res-filters" role="radiogroup" aria-label="Показать поля">
      {FILTERS.map((f) => {
        const count = f.value === "all" ? stats.total : stats[f.value] ?? 0;
        const active = value === f.value;
        const attention = f.attention && count > 0 && !active;
        return (
          <button
            key={f.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`res-filter${active ? " is-active" : ""}${attention ? " is-attention" : ""}`}
            onClick={() => onChange(f.value)}
          >
            {attention && <span className="res-dot" aria-hidden="true" />}
            {f.label} · {count}
          </button>
        );
      })}
    </div>
  );
};

const BADGES = {
  uncertain: { label: "Проверьте", cls: "is-warn" },
  partial: { label: "Частично", cls: "is-soft" },
  not_found: { label: "Не найдено", cls: "is-soft" },
};

const unwrap = (value) => {
  const wrapped = value !== null && typeof value === "object" && "data" in value;
  return {
    value: wrapped ? value.data : value,
    status: wrapped ? value.status : undefined,
    page: wrapped ? value.page : undefined,
  };
};

const isMultilineField = (fd) =>
  fd.data_type === "text" ||
  fd.key.includes("full_text") ||
  fd.key.includes("remarks") ||
  fd.key.includes("address");

const FieldInput = ({ fd, value, status, onChange, original = false, ...rest }) => {
  const cls = `res-input${original ? " is-original" : ""}${status === "uncertain" && !original ? " is-warn" : ""}${
    status === "not_found" && !value ? " is-missing" : ""
  }${fd.key.includes("mrz") ? " is-mono" : ""}`;
  return isMultilineField(fd) ? (
    <textarea className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
  ) : (
    <input type="text" className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
  );
};

/** Одна строка: подпись со статусом, оригинал и перевод рядом. */
export const FieldPairRow = ({ fd, original, translated, onFieldChange, categoryKey }) => {
  const o = unwrap(original);
  const t = unwrap(translated);
  const status = o.status || t.status;
  const badge = BADGES[status];
  const label = fd.display_name_ru || fd.display_name_en || fd.key;
  const id = `f-${categoryKey}-${fd.key}`;
  return (
    <div className="res-row">
      <div className="res-row-label">
        <label htmlFor={id}>{label}</label>
        {badge && <span className={`res-badge ${badge.cls}`}>{badge.label}</span>}
        {o.page && <span className="res-badge is-soft">Стр. {o.page}</span>}
      </div>
      <FieldInput
        fd={fd}
        value={o.value}
        status={status}
        original
        aria-label={`${label} — оригинал`}
        placeholder="—"
        onChange={(v) => onFieldChange([categoryKey, fd.key], v)}
      />
      <FieldInput
        fd={fd}
        id={id}
        value={t.value}
        status={status}
        placeholder={status === "not_found" ? "Введите вручную" : ""}
        onChange={(v) => onFieldChange([`${categoryKey}_translation`, fd.key], v)}
      />
    </div>
  );
};
